import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Loader } from "lucide-react";
import { toast } from "sonner";
import { organizationService } from "@/services/organizationService";
import type { OrgRole } from "@/types/organization";

interface RequireOrgRoleProps {
  /** Roles allowed to view the wrapped page (e.g. ["owner"] or ["owner", "admin"]). */
  allow: OrgRole[];
  children: React.ReactNode;
  /** Toast shown when access is denied. */
  deniedMessage?: string;
}

/**
 * Route guard that only renders its children when the signed-in user holds one
 * of the allowed roles in the organization referenced by the route (`:id` or
 * `:slug`). Anyone else is redirected to the org page (or /organizations).
 *
 * Wrap this inside SecureAuthGate so authentication is already guaranteed.
 */
export default function RequireOrgRole({ allow, children, deniedMessage }: RequireOrgRoleProps) {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  const orgKey = id || slug;
  // Stable dependency so an inline `allow` array prop doesn't re-run the effect every render.
  const allowKey = allow.join(",");
  const [status, setStatus] = useState<"checking" | "allowed" | "denied">("checking");

  useEffect(() => {
    let active = true;
    (async () => {
      if (!orgKey) {
        if (active) setStatus("denied");
        return;
      }
      try {
        const role = await organizationService.getMyRoleForOrg(orgKey);
        if (!active) return;
        if (role && allow.includes(role)) {
          setStatus("allowed");
        } else {
          toast.error(deniedMessage ?? "You don't have permission to access this page.");
          setStatus("denied");
        }
      } catch (error) {
        console.error("RequireOrgRole: role check failed", error);
        if (active) {
          toast.error("Unable to verify organization permissions.");
          setStatus("denied");
        }
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgKey, allowKey]);

  if (status === "checking") {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (status === "denied") {
    return <Navigate to={slug ? `/o/${slug}` : "/organizations"} replace />;
  }

  return <>{children}</>;
}
